import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../store/store';
import { updateObjective } from '../../store/map3PendingSlice';
import { AddEditDeleteBar1 } from '../stable/AddEditDeleteBar1';
import { ResultFlag } from '../stable/ResultFlag';
import { Button } from '../../components_catalyst/button';
import { Input } from '../../components_catalyst/input';
import { RadioGroup, RadioField, Radio } from '../../components_catalyst/radio';
import { Label } from '../../components_catalyst/fieldset';
import { Toast } from '../../components_misc/Toast';
import { useCurrentMap } from '../../hooks/useCurrentMap';
import { useRoleWithOverride } from '../../hooks/useRoleWithOverride';
import { getPermissionsForRole } from '../../utils/getPermissionsForRole';
import { createResult1 } from '@mm-shared/types/Result1.utils';
import type { Result1, Dependency1 } from '@mm-shared/types/Result1';
import {
  DRAWER_TAB_ACTIVE_CLASSES,
  DRAWER_TAB_INACTIVE_CLASSES,
} from '../stable/drawerTabStyles';

type ResultTabSubTab = 'info' | 'dependencies';

/**
 * ResultTab - Drawer component for viewing and editing individual result details.
 *
 * Accessed via route: objectives/:objectiveShortId/results/:resultShortId
 * Sub-tabs: Info | Dependencies
 */
export function ResultTab() {
  const [searchParams] = useSearchParams();
  const objectiveShortId = searchParams.get('objectiveId') ?? undefined;
  const resultShortId = searchParams.get('resultId') ?? undefined;
  const dispatch = useDispatch<AppDispatch>();

  const [activeSubTab, setActiveSubTab] = useState<ResultTabSubTab>('info');
  const [isEditingOwner, setIsEditingOwner] = useState(false);
  const [ownerInputValue, setOwnerInputValue] = useState('');
  const [isEditingDeadline, setIsEditingDeadline] = useState(false);
  const [deadlineInputValue, setDeadlineInputValue] = useState('');
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const objective = useSelector((state: RootState) =>
    state.map3Data.objectives.find((obj) => obj.shortId === objectiveShortId),
  );

  const result = objective?.results.find((r) => r.shortId === resultShortId);

  const currentMap = useCurrentMap();
  const userRole = useRoleWithOverride(currentMap?.uuid || '');
  const { canEdit } = getPermissionsForRole(userRole);

  if (!objective) {
    return <div className="p-4 text-gray-500">Objective not found</div>;
  }

  if (!result) {
    return <div className="p-4 text-gray-500">Result not found</div>;
  }

  const handleOwnerSave = async () => {
    const trimmed = ownerInputValue.trim();
    if (trimmed === result.ownerName) {
      setIsEditingOwner(false);
      return;
    }

    await dispatch(
      updateObjective({
        uuid: objective.uuid,
        updates: {
          results: objective.results.map((r) =>
            r.uuid === result.uuid
              ? createResult1({ ...r, ownerName: trimmed, ownerId: trimmed ? r.ownerId : '' })
              : r,
          ),
        },
      }),
    );
    setIsEditingOwner(false);
  };

  const handleDeadlineSave = async () => {
    const newDeadline = deadlineInputValue ? new Date(deadlineInputValue).toISOString() : undefined;
    if (newDeadline === result.deadline) {
      setIsEditingDeadline(false);
      return;
    }

    await dispatch(
      updateObjective({
        uuid: objective.uuid,
        updates: {
          results: objective.results.map((r) =>
            r.uuid === result.uuid ? createResult1({ ...r, deadline: newDeadline }) : r,
          ),
        },
      }),
    );
    setIsEditingDeadline(false);
  };

  const handleKeyResultChange = async (value: string) => {
    const newIsKeyResult = value === 'key-result';

    await dispatch(
      updateObjective({
        uuid: objective.uuid,
        updates: {
          results: objective.results.map((r) =>
            r.uuid === result.uuid ? createResult1({ ...r, isKeyResult: newIsKeyResult }) : r,
          ),
        },
      }),
    );
  };

  const formatDeadline = (deadline?: string): string => {
    if (!deadline) return 'No deadline set';
    try {
      return new Date(deadline).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return deadline;
    }
  };

  return (
    <div className="space-y-0">
      {/* Toast notification */}
      <Toast
        message={toastMessage}
        variant="info"
        position="top-right"
        autoClose
        onClose={() => setToastMessage(null)}
      />

      {/* Sub-tab navigation */}
      <nav className="bg-white border-b border-gray-200 mb-4" aria-label="Result Details">
        <div className="flex space-x-4 px-1">
          <button
            onClick={() => setActiveSubTab('info')}
            className={`inline-flex items-center border-b-2 px-1 pt-1 pb-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
              activeSubTab === 'info' ? DRAWER_TAB_ACTIVE_CLASSES : DRAWER_TAB_INACTIVE_CLASSES
            }`}
            aria-current={activeSubTab === 'info' ? 'page' : undefined}
          >
            Info
          </button>
          <button
            onClick={() => setActiveSubTab('dependencies')}
            className={`inline-flex items-center border-b-2 px-1 pt-1 pb-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
              activeSubTab === 'dependencies' ? DRAWER_TAB_ACTIVE_CLASSES : DRAWER_TAB_INACTIVE_CLASSES
            }`}
            aria-current={activeSubTab === 'dependencies' ? 'page' : undefined}
          >
            Dependencies
          </button>
        </div>
      </nav>

      {activeSubTab === 'info' && (
        <ResultInfoSubTab
          result={result}
          canEdit={canEdit}
          isEditingOwner={isEditingOwner}
          ownerInputValue={ownerInputValue}
          isEditingDeadline={isEditingDeadline}
          deadlineInputValue={deadlineInputValue}
          onOwnerEdit={() => {
            setOwnerInputValue(result.ownerName || '');
            setIsEditingOwner(true);
          }}
          onOwnerChange={setOwnerInputValue}
          onOwnerSave={handleOwnerSave}
          onOwnerCancel={() => setIsEditingOwner(false)}
          onDeadlineEdit={() => {
            // Convert ISO string to YYYY-MM-DD for input[type=date]
            const isoDate = result.deadline ? result.deadline.slice(0, 10) : '';
            setDeadlineInputValue(isoDate);
            setIsEditingDeadline(true);
          }}
          onDeadlineChange={setDeadlineInputValue}
          onDeadlineSave={handleDeadlineSave}
          onDeadlineCancel={() => setIsEditingDeadline(false)}
          onKeyResultChange={handleKeyResultChange}
          formatDeadline={formatDeadline}
        />
      )}

      {activeSubTab === 'dependencies' && (
        <ResultDependenciesSubTab dependencies={result.dependencies} />
      )}
    </div>
  );
}

interface ResultInfoSubTabProps {
  result: Result1;
  canEdit: boolean;
  isEditingOwner: boolean;
  ownerInputValue: string;
  isEditingDeadline: boolean;
  deadlineInputValue: string;
  onOwnerEdit: () => void;
  onOwnerChange: (value: string) => void;
  onOwnerSave: () => void;
  onOwnerCancel: () => void;
  onDeadlineEdit: () => void;
  onDeadlineChange: (value: string) => void;
  onDeadlineSave: () => void;
  onDeadlineCancel: () => void;
  onKeyResultChange: (value: string) => void;
  formatDeadline: (deadline?: string) => string;
}

function ResultInfoSubTab({
  result,
  canEdit,
  isEditingOwner,
  ownerInputValue,
  isEditingDeadline,
  deadlineInputValue,
  onOwnerEdit,
  onOwnerChange,
  onOwnerSave,
  onOwnerCancel,
  onDeadlineEdit,
  onDeadlineChange,
  onDeadlineSave,
  onDeadlineCancel,
  onKeyResultChange,
  formatDeadline,
}: ResultInfoSubTabProps) {
  // Dynamic deadline label: "Milestone" if isKeyResult, "Inchstone" otherwise
  const deadlineLabel = result.isKeyResult ? 'Milestone' : 'Inchstone';

  return (
    <div className="space-y-6 p-4">
      {/* Type — isKeyResult radio */}
      <section
        tabIndex={0}
        className="focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-md"
        aria-labelledby="result-type-heading"
      >
        <h2 id="result-type-heading" className="text-lg font-semibold mb-2">
          Type
        </h2>
        <RadioGroup
          value={result.isKeyResult ? 'key-result' : 'result'}
          onChange={canEdit ? onKeyResultChange : undefined}
          aria-label="Result type"
          className="space-y-2"
        >
          <RadioField>
            <Radio value="key-result" disabled={!canEdit} />
            <Label className="inline-flex items-center gap-x-1.5 whitespace-nowrap">
              <ResultFlag isKeyResult={true} className="h-4 w-4 text-blue-600 flex-shrink-0" />
              <span>Key Result (deadline is a milestone for a deliverable)</span>
            </Label>
          </RadioField>
          <RadioField>
            <Radio value="result" disabled={!canEdit} />
            <Label className="inline-flex items-center gap-x-1.5 whitespace-nowrap">
              <ResultFlag isKeyResult={false} className="h-4 w-4 text-blue-600 flex-shrink-0" />
              <span>Result (deadline is an inchstone to mark progress)</span>
            </Label>
          </RadioField>
        </RadioGroup>
      </section>

      {/* Deadline */}
      <section
        tabIndex={0}
        className="focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-md"
        aria-labelledby="result-deadline-heading"
      >
        <h2 id="result-deadline-heading" className="text-lg font-semibold mb-2">
          {deadlineLabel}
        </h2>
        <div className="p-2 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/20 relative">
          {isEditingDeadline ? (
            <div className="flex items-center gap-2">
              <Input
                type="date"
                value={deadlineInputValue}
                onChange={(e) => onDeadlineChange((e.target as HTMLInputElement).value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    onDeadlineSave();
                  } else if (e.key === 'Escape') {
                    onDeadlineCancel();
                  }
                }}
                autoFocus
                aria-label={`Edit ${deadlineLabel.toLowerCase()}`}
              />
              <Button plain onClick={onDeadlineSave}>
                Save
              </Button>
              <Button plain onClick={onDeadlineCancel}>
                Cancel
              </Button>
            </div>
          ) : (
            <div className="flex justify-between items-center">
              <span>{formatDeadline(result.deadline)}</span>
              <AddEditDeleteBar1
                isVisible={true}
                showAdd={false}
                showEdit={canEdit}
                showDelete={false}
                showInfo={false}
                onEdit={onDeadlineEdit}
              />
            </div>
          )}
        </div>
      </section>

      {/* Owner Section */}
      <section
        tabIndex={0}
        className="focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-md"
        aria-labelledby="result-owner-heading"
      >
        <h2 id="result-owner-heading" className="text-lg font-semibold mb-2">
          Owner
        </h2>
        <div className="p-2 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/20 relative">
          {isEditingOwner ? (
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={ownerInputValue}
                onChange={(e) => onOwnerChange(e.target.value)}
                onBlur={onOwnerSave}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    onOwnerSave();
                  } else if (e.key === 'Escape') {
                    onOwnerCancel();
                  }
                }}
                className="flex-1 px-2 py-1 rounded border border-gray-300 dark:bg-zinc-700 dark:text-white"
                autoFocus
                placeholder="Enter owner name..."
                aria-label="Edit owner name"
              />
              <Button plain onClick={onOwnerCancel}>
                Cancel
              </Button>
            </div>
          ) : (
            <div className="flex justify-between items-center">
              <span>{result.ownerName || 'No owner assigned'}</span>
              <AddEditDeleteBar1
                isVisible={true}
                showAdd={false}
                showEdit={canEdit}
                showDelete={false}
                showInfo={false}
                onEdit={onOwnerEdit}
              />
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

interface ResultDependenciesSubTabProps {
  dependencies?: Dependency1[];
}

function ResultDependenciesSubTab({ dependencies }: ResultDependenciesSubTabProps) {
  if (!dependencies || dependencies.length === 0) {
    return (
      <div className="p-4">
        <p className="text-gray-500 text-sm" aria-label="No dependencies">
          No dependencies
        </p>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-2" aria-label="Dependencies list">
      <h2 className="text-lg font-semibold mb-3">Dependencies</h2>
      <ul className="space-y-2" role="list">
        {dependencies.map((dep, index) => (
          <li
            key={`${dep.targetResultUuid}-${index}`}
            className="p-2 rounded-md bg-gray-50 dark:bg-zinc-800 text-sm"
            role="listitem"
          >
            <div className="font-medium text-gray-700 dark:text-gray-300">
              Result: {dep.targetResultUuid.slice(0, 10)}
            </div>
            <div className="text-gray-500 text-xs">
              Objective: {dep.targetObjectiveUuid.slice(0, 10)}
            </div>
            <div className="text-gray-500 text-xs">Map: {dep.targetMapUuid.slice(0, 10)}</div>
          </li>
        ))}
      </ul>
    </div>
  );
}
